import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  workspaceApi,
  setTenantSlug,
  setAuthToken,
  isAppsHost,
  type WorkspaceInfo,
  type AuthUser,
} from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

type Step = 'email' | 'otp' | 'workspaces' | 'workspace-slug';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Workspace-first flow state
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState<string | null>(null);
  const [slugInput, setSlugInput] = useState('');

  // Multi-workspace state
  const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([]);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  // Subdomain-based workspace detection
  const [subdomainSlug, setSubdomainSlug] = useState<string | null>(null);

  // On mount: detect tenant subdomain and pre-configure workspace
  useEffect(() => {
    const host = window.location.hostname;
    if (host.endsWith('.tabillo.com')) {
      const slug = host.slice(0, host.indexOf('.'));
      // 'master' is a real internal tenant — exclude only infrastructure subdomains
      const reserved = ['admin', 'api', 'www', 'mail', 'help', 'support', 'status'];
      if (!reserved.includes(slug)) {
        workspaceApi.checkWorkspace(slug).then(res => {
          if (res.valid) {
            setSubdomainSlug(slug);
            setSelectedSlug(slug);
            setSelectedWorkspaceName(res.name || slug);
            setStep('email');
          }
        }).catch(() => {}); // ignore errors, fall back to generic login
      }
    }
  }, []);

  // Redirect if already authenticated
  if (isAuthenticated) {
    const from = (location.state as any)?.from?.pathname || '/home';
    navigate(from, { replace: true });
    return null;
  }

  const handleLogin = (user: AuthUser, token: string, slug: string) => {
    if (isAppsHost()) {
      // Cross-domain redirect — token stored at destination domain, not here
      window.location.href = `https://${slug}.tabillo.com/#auth_token=${token}`;
      return;
    }
    setTenantSlug(slug);
    setAuthToken(token);
    login(user);
    const from = (location.state as any)?.from?.pathname || '/home';
    navigate(from, { replace: true });
  };

  // ── Email Step ──
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      setNotFound(false);
      await workspaceApi.sendLoginOTP(
        email.toLowerCase().trim(),
        selectedSlug || undefined,
      );
      setStep('otp');
    } catch (err: any) {
      const raw = err.message || '';
      if (raw.toLowerCase().includes('no account')) {
        setNotFound(true);
        setError('We did not find any workspace(s) associated to this email.');
      } else {
        setError(raw || 'Failed to send login code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── OTP Step ──
  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await workspaceApi.verifyLoginOTP(
        email.toLowerCase().trim(),
        otp,
      );

      if (response.token && response.user && response.slug) {
        // Single workspace or workspace-first — auto-login
        handleLogin(response.user, response.token, response.slug);
      } else if (response.workspaces && response.sessionToken) {
        // Multiple workspaces — show picker
        setWorkspaces(response.workspaces);
        setSessionToken(response.sessionToken);
        setStep('workspaces');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid code');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Workspace Picker ──
  const handleSelectWorkspace = async (slug: string) => {
    if (!sessionToken) return;
    setError('');
    setIsLoading(true);

    try {
      const response = await workspaceApi.selectWorkspace(sessionToken, slug);
      handleLogin(response.user, response.token, response.slug);
    } catch (err: any) {
      setError(err.message || 'Failed to select workspace');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Workspace Slug Step ──
  const handleSlugSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await workspaceApi.checkWorkspace(slugInput.toLowerCase().trim());
      if (response.valid && response.name) {
        setSelectedSlug(slugInput.toLowerCase().trim());
        setSelectedWorkspaceName(response.name);
        setStep('email');
      } else {
        setError('Workspace not found. Please check the URL and try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check workspace');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setIsLoading(true);

    try {
      await workspaceApi.sendLoginOTP(
        email.toLowerCase().trim(),
        selectedSlug || undefined,
      );
      setOtp('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setStep('email');
    setOtp('');
    setError('');
  };

  const handleBackToEmail = () => {
    setStep('email');
    setSelectedSlug(null);
    setSelectedWorkspaceName(null);
    setSlugInput('');
    setEmail('');
    setOtp('');
    setError('');
    setWorkspaces([]);
    setSessionToken(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-gray-900" style={{ letterSpacing: '-0.04em' }}>Tabillo</h1>
          <p className="mt-2 text-gray-600">Product Roadmap Planning</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">

          {/* ── WORKSPACE SLUG STEP ── */}
          {step === 'workspace-slug' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Sign in to your workspace
              </h2>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Enter your workspace URL to continue
              </p>

              <form onSubmit={handleSlugSubmit} className="space-y-4">
                <div>
                  <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                    Workspace URL
                  </label>
                  <div className="flex items-center">
                    <input
                      id="slug"
                      type="text"
                      value={slugInput}
                      onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      placeholder="your-company"
                      required
                      autoFocus
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-sm text-gray-500">
                      .tabillo.com
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !slugInput}
                  className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Checking...' : 'Continue'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={handleBackToEmail}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Back to sign in with email
                </button>
              </div>
            </>
          )}

          {/* ── EMAIL STEP ── */}
          {step === 'email' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                {selectedWorkspaceName
                  ? `Sign in to ${selectedWorkspaceName}`
                  : 'Sign in to your account'}
              </h2>
              {selectedWorkspaceName && (
                <p className="text-sm text-gray-500 mb-4 text-center">
                  {selectedSlug}.tabillo.com
                </p>
              )}
              {!selectedWorkspaceName && (
                <p className="text-sm text-gray-600 mb-6 text-center">
                  Enter your email to get started
                </p>
              )}

              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {error}
                    {notFound && (
                      <span> <Link to="/signup" className="underline font-medium">Sign up now</Link></span>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Sending...' : 'Continue with Email'}
                </button>
              </form>

              {selectedSlug && !subdomainSlug && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleBackToEmail}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Use a different workspace
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── OTP STEP ── */}
          {step === 'otp' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Check your email
              </h2>
              <p className="text-sm text-gray-600 mb-6 text-center">
                {isAppsHost()
                  ? <>We found your account. A one-time password has been sent to<br /><span className="font-medium text-gray-900">{email}</span></>
                  : <>We sent a 6-digit code to<br /><span className="font-medium text-gray-900">{email}</span></>
                }
              </p>

              <form onSubmit={handleOTPSubmit} className="space-y-4">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                    Login code
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    required
                    autoFocus
                    maxLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest font-mono"
                  />
                </div>

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || otp.length !== 6}
                  className="w-full py-2 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Verifying...' : 'Verify Code'}
                </button>
              </form>

              <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                <button
                  onClick={handleResendOTP}
                  disabled={isLoading}
                  className="text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={handleChangeEmail}
                  disabled={isLoading}
                  className="text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50"
                >
                  Change email
                </button>
              </div>
            </>
          )}

          {/* ── WORKSPACE PICKER STEP ── */}
          {step === 'workspaces' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                Choose a workspace
              </h2>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Your email has access to multiple workspaces
              </p>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {workspaces.map((ws) => (
                  <button
                    key={ws.slug}
                    onClick={() => handleSelectWorkspace(ws.slug)}
                    disabled={isLoading}
                    className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-medium text-gray-900">{ws.name}</div>
                    {!isAppsHost() && (
                      <div className="text-sm text-gray-500">{ws.slug}.tabillo.com</div>
                    )}
                  </button>
                ))}
              </div>

              <p className="mt-4 text-xs text-gray-500 text-center">
                Not seeing your workspace? Contact your organization admin.
              </p>

              <div className="mt-4 text-center">
                <button
                  onClick={handleBackToEmail}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Use a different email
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer links */}
        <div className="mt-6 space-y-2 text-center">
          {step !== 'workspace-slug' && !subdomainSlug && (
            <p className="text-sm text-gray-500">
              <button
                onClick={() => {
                  setStep('workspace-slug');
                  setError('');
                  setSelectedSlug(null);
                  setSelectedWorkspaceName(null);
                }}
                className="text-blue-600 hover:text-blue-700 hover:underline"
              >
                Sign in to your workspace
              </button>
            </p>
          )}
          <p className="text-sm text-gray-500">
            Don't have an organization yet?{' '}
            <Link to="/signup" className="text-blue-600 hover:text-blue-700 hover:underline">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
